import Phaser from 'phaser';
// Fredoka bundled locally (no CDN) — rounded, candy-friendly. Weights match the
// document.fonts.load() preload below and every Text style in the scenes.
import '@fontsource/fredoka/400.css';
import '@fontsource/fredoka/500.css';
import '@fontsource/fredoka/600.css';
import '@fontsource/fredoka/700.css';
import { gameConfig } from './config';
import { initAnalytics } from './services/analytics';

// Wait for Fredoka to finish loading before booting. Phaser rasterizes each
// Text object to a texture at creation time; if the webfont isn't ready yet,
// that texture bakes in the fallback system font and never refreshes for
// static labels — a main cause of soft / inconsistent HUD text. A short
// timeout guards against a slow/blocked font CDN so we never hang on boot.
async function ensureFonts(): Promise<void> {
  if (typeof document === 'undefined' || !('fonts' in document)) return;
  try {
    await Promise.race([
      Promise.all([
        ...['400', '500', '600', '700'].map((w) => document.fonts.load(`${w} 24px Fredoka`)),
        document.fonts.ready,
      ]),
      new Promise((resolve) => setTimeout(resolve, 2500)),
    ]);
  } catch {
    /* system-ui fallback is acceptable; don't block boot on font errors */
  }
}

// Boot the game once the DOM is ready.
async function boot(): Promise<void> {
  initAnalytics();
  await ensureFonts();
  const game = new Phaser.Game(gameConfig);
  if (import.meta.env.DEV) {
    (window as unknown as { __shmergeGame?: Phaser.Game }).__shmergeGame = game;
  }
  // eslint-disable-next-line no-console
  console.log(`[shmerge] v${__APP_VERSION__} built ${__BUILD_DATE__}`);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
